// Hero Slides Manager Component
import React, { useState, useEffect, useRef } from 'react';
import { Plus, Trash2, Loader2, Upload, Save, X, ArrowUp, ArrowDown } from 'lucide-react';
import { Button } from '../ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const HeroSlideForm = ({ form, setForm, onSave, onCancel }) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch(`${BACKEND_URL}/api/admin/upload`, { method: 'POST', credentials: 'include', body: formData });
      if (response.ok) {
        const data = await response.json();
        setForm({...form, image_url: `${BACKEND_URL}${data.url}`});
      }
    } catch (err) { console.error('Upload error:', err); }
    finally { setUploading(false); }
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Image du slide</label>
        {form.image_url && (
          <div className="relative w-full h-40 bg-slate-100 rounded-lg overflow-hidden mb-2">
            <img src={form.image_url} alt="Preview" className="w-full h-full object-cover" />
            <button type="button" onClick={() => setForm({...form, image_url: ''})} className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"><X className="w-3 h-3" /></button>
          </div>
        )}
        <div className="flex gap-2">
          <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileUpload} className="hidden" />
          <Button type="button" variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
            {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Upload...</> : <><Upload className="w-4 h-4 mr-2" /> Uploader</>}
          </Button>
          <input type="text" value={form.image_url || ''} onChange={(e) => setForm({...form, image_url: e.target.value})} placeholder="ou coller une URL..." className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm" />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">🇫🇷 Titre FR (optionnel)</label>
          <input type="text" value={form.title_fr || ''} onChange={(e) => setForm({...form, title_fr: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">🇬🇧 Titre EN (optionnel)</label>
          <input type="text" value={form.title_en || ''} onChange={(e) => setForm({...form, title_en: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">🇩🇿 العنوان AR (optionnel)</label>
          <input type="text" value={form.title_ar || ''} onChange={(e) => setForm({...form, title_ar: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm text-right" dir="rtl" />
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onCancel}><X className="w-4 h-4 mr-1" /> Annuler</Button>
        <Button size="sm" onClick={onSave} disabled={!form.image_url}><Save className="w-4 h-4 mr-1" /> Enregistrer</Button>
      </div>
    </div>
  );
};

const HeroSlidesManager = () => {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);

  useEffect(() => { fetchSlides(); }, []);

  const fetchSlides = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/hero-slides`, { credentials: 'include' });
      if (response.ok) { const data = await response.json(); setSlides((data || []).sort((a, b) => (a.order || 0) - (b.order || 0))); }
    } catch (err) { console.error('Error:', err); }
    finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette image du slider ?')) return;
    try { await fetch(`${BACKEND_URL}/api/admin/hero-slides/${id}`, { method: 'DELETE', credentials: 'include' }); fetchSlides(); }
    catch (err) { console.error('Error:', err); }
  };

  const handleSave = async (id) => {
    try { await fetch(`${BACKEND_URL}/api/admin/hero-slides/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(editForm) }); setEditingId(null); fetchSlides(); }
    catch (err) { console.error('Error:', err); }
  };

  const handleAdd = async () => {
    try { await fetch(`${BACKEND_URL}/api/admin/hero-slides`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(editForm) }); setShowAddForm(false); setEditForm({}); fetchSlides(); }
    catch (err) { console.error('Error:', err); }
  };

  const handleMoveSlide = async (slideId, direction) => {
    const currentIndex = slides.findIndex(s => s.id === slideId);
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= slides.length) return;
    const currentSlide = slides[currentIndex];
    const targetSlide = slides[newIndex];
    try {
      await fetch(`${BACKEND_URL}/api/admin/hero-slides/${currentSlide.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ ...currentSlide, order: targetSlide.order || newIndex }) });
      await fetch(`${BACKEND_URL}/api/admin/hero-slides/${targetSlide.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ ...targetSlide, order: currentSlide.order || currentIndex }) });
      fetchSlides();
    } catch (err) { console.error('Error:', err); }
  };

  if (loading) { return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>; }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-light text-slate-800">Slider d'Accueil</h2>
          <p className="text-sm text-slate-500 mt-1">Gérez les images du carousel de la page d'accueil</p>
        </div>
        <Button onClick={() => { setShowAddForm(true); setEditForm({ image_url: '', title_en: '', title_fr: '', title_ar: '', order: slides.length, is_active: true }); }}>
          <Plus className="w-4 h-4 mr-2" /> Ajouter
        </Button>
      </div>

      {showAddForm && (
        <div className="border border-green-200 bg-green-50 rounded-lg p-4 mb-6">
          <h3 className="font-medium text-slate-800 mb-4">Nouvelle Image</h3>
          <HeroSlideForm form={editForm} setForm={setEditForm} onSave={handleAdd} onCancel={() => setShowAddForm(false)} />
        </div>
      )}

      <div className="space-y-4">
        {slides.map((slide, index) => (
          <div key={slide.id} className="border border-slate-200 rounded-lg p-4">
            {editingId === slide.id ? (
              <HeroSlideForm form={editForm} setForm={setEditForm} onSave={() => handleSave(slide.id)} onCancel={() => setEditingId(null)} />
            ) : (
              <div className="flex items-center gap-4">
                <div className="flex flex-col gap-1">
                  <button onClick={() => handleMoveSlide(slide.id, 'up')} disabled={index === 0} className="p-1 hover:bg-slate-100 rounded disabled:opacity-30"><ArrowUp className="w-4 h-4" /></button>
                  <button onClick={() => handleMoveSlide(slide.id, 'down')} disabled={index === slides.length - 1} className="p-1 hover:bg-slate-100 rounded disabled:opacity-30"><ArrowDown className="w-4 h-4" /></button>
                </div>
                <img src={slide.image_url} alt="" className="w-40 h-24 object-cover rounded" />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-medium text-slate-800">#{index + 1}</span>
                    {slide.title_fr && <span className="text-sm text-slate-500">- {slide.title_fr}</span>}
                    {!slide.is_active && <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs">Inactif</span>}
                  </div>
                  <p className="text-xs text-slate-400 mt-1 truncate">{slide.image_url}</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => { setEditingId(slide.id); setEditForm(slide); }}>Modifier</Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(slide.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      {slides.length === 0 && !showAddForm && <div className="text-center py-12 text-slate-500">Aucune image dans le slider.</div>}
    </div>
  );
};

export default HeroSlidesManager;
